// src/components/CreditsUsage.jsx
//
// OpenAI-style credit balance + usage: remaining credit, active plan, usage
// this month (tokens, spend, requests), a 30-day chart, a per-task
// breakdown, a ledger, and a one-click £10 top-up.
import { useCallback, useEffect, useState } from "react";
import apiService from "../services/api";
import { Button } from "../arc/components/button/button";
import "./CreditsUsage.css";

export const CREDITS_CHANGED_EVENT = "axo:credits-changed";

const gbp = (n, dp = 2) =>
  `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const compact = (n) =>
  Number(n || 0).toLocaleString("en-GB", {
    notation: "compact",
    maximumFractionDigits: 1,
  });
const TASK_LABELS = {
  outreach_draft: "Outreach drafts",
  revenue_briefing: "Daily briefing",
  company_research: "Company research",
  reply_classification: "Reply handling",
};
const taskLabel = (t) =>
  TASK_LABELS[t] ||
  String(t || "Other")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
const dateLabel = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

export function useCredits({ days = 30 } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await apiService.getCredits({ days });
      setData(res.data || null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't load credit usage");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
    window.addEventListener(CREDITS_CHANGED_EVENT, load);
    return () => window.removeEventListener(CREDITS_CHANGED_EVENT, load);
  }, [load]);

  return { data, error, loading, reload: load };
}

/** Small balance pill for the navy header. Click opens Billing. */
export function CreditBalancePill({ onClick }) {
  const { data } = useCredits({ days: 1 });
  if (!data || (!data.canTopUp && !data.unlimited)) return null;
  return (
    <button
      type="button"
      className={`cu-pill ${data.status !== "ok" ? data.status : ""}`}
      onClick={onClick}
      aria-label={`AI credit balance ${gbp(data.balanceGbp)}. Open billing`}
      title="AI credit balance"
    >
      {data.unlimited ? "Unlimited" : gbp(data.balanceGbp)}
      <span style={{ opacity: 0.6, fontWeight: 400 }}>credit</span>
    </button>
  );
}

export default function CreditsUsage({ canManageBilling = true }) {
  const { data, error, loading } = useCredits();
  const [busy, setBusy] = useState(false);
  const [topUpError, setTopUpError] = useState(null);

  const topUp = async () => {
    setBusy(true);
    setTopUpError(null);
    try {
      const res = await apiService.startCreditTopUp();
      const url = res.data?.checkoutUrl;
      if (!url) throw new Error("No checkout URL");
      window.location.assign(url);
    } catch (err) {
      setTopUpError(
        err.response?.data?.message ||
          "Couldn't start the top-up. Please try again.",
      );
      setBusy(false);
    }
  };

  if (loading)
    return (
      <div className="cu-card">
        <div className="cu-sub">Loading credit usage…</div>
      </div>
    );
  if (error || !data)
    return (
      <div className="cu-card">
        <div className="cu-sub">{error || "Credit usage is unavailable."}</div>
      </div>
    );

  const {
    balanceGbp,
    includedMonthlyGbp,
    status,
    plan,
    month,
    byTask,
    byDay,
    ledger,
    topUpAmountGbp,
  } = data;
  const meterPct =
    includedMonthlyGbp > 0
      ? Math.max(0, Math.min(100, (balanceGbp / includedMonthlyGbp) * 100))
      : 0;
  const peak = Math.max(0.0001, ...byDay.map((d) => d.costGbp));
  const taskPeak = Math.max(0.0001, ...byTask.map((t) => t.costGbp));
  const statusText =
    status === "unlimited"
      ? "Unlimited"
      : status === "empty"
        ? "Out of credit"
        : status === "low"
          ? "Running low"
          : "Healthy";

  return (
    <div className="cu">
      {status !== "ok" && status !== "unlimited" && (
        <div
          className={`cu-banner ${status === "empty" ? "err" : "warn"}`}
          role="status"
        >
          <span>
            {status === "empty"
              ? "You're out of AI credit. AI drafting, briefings and research are paused until you top up."
              : `Only ${gbp(balanceGbp)} of AI credit left.`}
          </span>
          {data.canTopUp && canManageBilling && (
            <Button size="sm" onClick={topUp} loading={busy}>
              Add {gbp(topUpAmountGbp, 0)}
            </Button>
          )}
        </div>
      )}

      <div className="cu-grid">
        <div className="cu-card">
          <h3 className="cu-title">Credit balance</h3>
          <div className="cu-sub">
            {data.unlimited
              ? "This account has unlimited AI credit. Usage is still tracked below."
              : "Your remaining credit carries forward each month. Top-ups never expire."}
          </div>
          <div className="cu-balance">
            <div>
              <div className="cu-amount-label">Remaining</div>
              <div className="cu-amount">
                {data.unlimited ? "Unlimited" : gbp(balanceGbp)}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                className={`cu-chip ${status === "ok" || status === "unlimited" ? "" : status}`}
              >
                <i />
                {statusText}
              </span>
              {data.canTopUp && canManageBilling && (
                <Button onClick={topUp} loading={busy}>
                  Add {gbp(topUpAmountGbp, 0)} credit
                </Button>
              )}
            </div>
          </div>
          {includedMonthlyGbp > 0 && !data.unlimited && (
            <>
              <div className="cu-meter" aria-hidden="true">
                <div style={{ width: `${meterPct}%` }} />
              </div>
              <div className="cu-meter-note">
                <span>{gbp(balanceGbp)} left</span>
                <span>{gbp(includedMonthlyGbp, 0)} starting credit</span>
              </div>
            </>
          )}
          {!canManageBilling && data.canTopUp && (
            <div className="cu-sub" style={{ marginTop: 12 }}>
              Only your account owner can add credit.
            </div>
          )}
          {topUpError && (
            <div
              className="cu-sub"
              role="alert"
              style={{ color: "var(--red)", marginTop: 12 }}
            >
              {topUpError}
            </div>
          )}
        </div>

        <div className="cu-card">
          <h3 className="cu-title">Active plan</h3>
          <div className="cu-plan-name">{plan.name}</div>
          <div className="cu-plan-row">
            <span>Price</span>
            <b>
              {plan.priceGbp != null
                ? `${gbp(plan.priceGbp, 0)}/month`
                : "Custom"}
            </b>
          </div>
          <div className="cu-plan-row">
            <span>Status</span>
            <b>{plan.isActive ? "Active" : "Inactive"}</b>
          </div>
          <div className="cu-plan-row">
            <span>Starting credit</span>
            <b>
              {includedMonthlyGbp > 0 ? gbp(includedMonthlyGbp, 0) : "None"}
            </b>
          </div>
          <div className="cu-plan-row">
            <span>Unused credit</span>
            <b>{includedMonthlyGbp > 0 ? "Carries forward" : "n/a"}</b>
          </div>
          <div className="cu-plan-row">
            <span>Top-up</span>
            <b>
              {data.canTopUp ? `${gbp(topUpAmountGbp, 0)} per pack` : "n/a"}
            </b>
          </div>
        </div>
      </div>

      <div className="cu-card">
        <h3 className="cu-title">Usage this month</h3>
        <div className="cu-sub">
          Counted from the real token usage reported by the AI provider.
        </div>
        <div className="cu-stats">
          <div className="cu-stat">
            <span>Spend</span>
            <b>{gbp(month.costGbp)}</b>
          </div>
          <div className="cu-stat">
            <span>Input tokens</span>
            <b>{compact(month.promptTokens)}</b>
          </div>
          <div className="cu-stat">
            <span>Output tokens</span>
            <b>{compact(month.completionTokens)}</b>
          </div>
          <div className="cu-stat">
            <span>Requests</span>
            <b>{compact(month.requests)}</b>
          </div>
        </div>
        {byDay.length > 0 ? (
          <>
            <div
              className="cu-chart"
              role="img"
              aria-label="Daily spend, last 30 days"
            >
              {byDay.map((d) => (
                <div
                  key={d.day}
                  className="cu-bar"
                  style={{
                    height: `${Math.max(3, (d.costGbp / peak) * 100)}%`,
                  }}
                  title={`${dateLabel(d.day)}: ${gbp(d.costGbp, 4)} · ${compact(d.tokens)} tokens`}
                />
              ))}
            </div>
            <div className="cu-axis">
              <span>{dateLabel(byDay[0].day)}</span>
              <span>{dateLabel(byDay[byDay.length - 1].day)}</span>
            </div>
          </>
        ) : (
          <div className="cu-empty">No AI usage yet.</div>
        )}
      </div>

      {byTask.length > 0 && (
        <div className="cu-card">
          <h3 className="cu-title">Where it went</h3>
          <div className="cu-sub">This month, by feature.</div>
          <div style={{ marginTop: 8 }}>
            {byTask.map((t) => (
              <div className="cu-task" key={t.taskType}>
                <span>
                  {taskLabel(t.taskType)}
                  <br />
                  <small>
                    {compact(t.tokens)} tokens · {t.requests} requests
                  </small>
                </span>
                <b>{gbp(t.costGbp)}</b>
                <div className="cu-meter">
                  <div style={{ width: `${(t.costGbp / taskPeak) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="cu-card">
        <h3 className="cu-title">Credit history</h3>
        {ledger.length === 0 ? (
          <div className="cu-empty">Nothing yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="cu-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th className="r">Amount</th>
                  <th className="r">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((r) => (
                  <tr key={r.id}>
                    <td>{dateLabel(r.createdAt)}</td>
                    <td>
                      {r.description || r.type}
                      {r.tokens ? (
                        <span style={{ color: "var(--text-muted)" }}>
                          {" "}
                          · {compact(r.tokens)} tokens
                        </span>
                      ) : null}
                    </td>
                    <td className={`r ${r.amountGbp > 0 ? "cu-pos" : ""}`}>
                      {r.amountGbp > 0 ? "+" : "−"}
                      {gbp(
                        Math.abs(r.amountGbp),
                        Math.abs(r.amountGbp) < 0.01 ? 4 : 2,
                      )}
                    </td>
                    <td className="r">{gbp(r.balanceAfterGbp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
